import React, { useState } from "react";
import { ChevronLeft, ChevronRight, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { AppSidebar } from "../components/app-sidebar";
import { SiteHeader } from "../components/site-header";
import { SidebarInset, SidebarProvider } from "../components/ui/sidebar";
import { Loader } from "../components/ui/loader";
import { useSessionAuditLogs, SessionAuditEntry } from "../lib/api";
import { cn } from "../lib/utils";

const isCritical = (entry: SessionAuditEntry) =>
  (entry.responseStatus != null && entry.responseStatus >= 400) ||
  (entry.event != null && entry.event.endsWith("_FAILED"));

// ─── Date grouping ────────────────────────────────────────────────────────

interface DayGroup {
  date: string;           // "Mar 24, 2026"
  entries: SessionAuditEntry[];
  criticalCount: number;
}

const groupByDate = (entries: SessionAuditEntry[]): DayGroup[] => {
  const map = new Map<string, SessionAuditEntry[]>();

  entries.forEach((e) => {
    // Backend renders the date per the DATE_TIME_FORMAT utility setting — group on it as-is.
    const key = e.date;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(e);
  });

  return Array.from(map.entries()).map(([date, items]) => ({
    date,
    entries: items,
    criticalCount: items.filter(isCritical).length,
  }));
};

// ─── Component ────────────────────────────────────────────────────────────

const SessionActivityPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const size = 50;

  // The backend returns ALL users' session events — the activity log is org-visible.
  const { data, isLoading } = useSessionAuditLogs(page, size);

  const groups = data ? groupByDate(data.data) : [];

  return (
    <SidebarProvider
      style={{
        "--sidebar-width": "16rem",
        "--header-height": "3rem",
      } as React.CSSProperties}
      className="min-h-screen"
    >
      <AppSidebar variant="inset" />
      <SidebarInset className="pt-0">
        <SiteHeader />
        <div className="flex flex-1 flex-col p-4">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-foreground">Session Activity</h1>
            <p className="text-muted-foreground mt-1">
              Login, logout, and account events across all MU users
            </p>
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <Loader className="w-8 h-8" />
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No session activity recorded yet.
            </div>
          ) : (
            <>
              {/* Timeline */}
              <div className="space-y-6">
                {groups.map((group) => (
                  <div key={group.date}>
                    {/* Day header */}
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-sm font-semibold text-foreground">{group.date}</span>
                      <span className="text-sm text-muted-foreground">
                        {group.entries.length} {group.entries.length === 1 ? "activity" : "activities"}
                      </span>
                      {group.criticalCount > 0 && (
                        <span className="flex items-center gap-1 text-sm text-red-500">
                          <AlertTriangle className="h-3.5 w-3.5" />
                          {group.criticalCount} Critical
                        </span>
                      )}
                    </div>

                    {/* Entries */}
                    <div className="relative ml-1 border-l border-dashed border-muted pl-4 space-y-1.5 pb-2">
                      {group.entries.map((entry) => {
                        const time = entry.time;
                        const critical = isCritical(entry);
                        return (
                          <div key={entry.id} className="flex items-start gap-3">
                            {/* Dot */}
                            <span
                              className={cn(
                                "absolute -left-1.5 mt-1.5 h-2 w-2 rounded-full border",
                                critical
                                  ? "border-red-400 bg-red-400"
                                  : "border-muted-foreground bg-background"
                              )}
                            />
                            <span className="text-xs text-muted-foreground w-16 shrink-0">{time}</span>
                            <span className={cn("text-sm", critical ? "text-red-500" : "text-foreground")}>
                              {entry.sentence ?? entry.event ?? "Unknown action"}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {data && data.totalPages > 1 && (
                <div className="flex items-center justify-between mt-6">
                  <div className="text-xs text-muted-foreground">
                    Page {data.page + 1} of {data.totalPages}
                  </div>
                  <div className="flex items-center space-x-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2"
                      disabled={data.first}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      <ChevronLeft className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2"
                      disabled={data.last}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      <ChevronRight className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
};

export default SessionActivityPage;